import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Youtube, Twitter, Twitch, ArrowRight, Shield } from "lucide-react";
import { NAV, NAV_MORE, TOK_LOGO } from "@/lib/data";
import { SOCIAL_LINKS } from "@/components/SocialFunnel";
import { getTrustBandPartners } from "@/lib/partners";
import PhilosophyQuote from "@/components/PhilosophyQuote";
import StayConnected from "@/components/StayConnected";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success("You're on the list. Welcome to the world.");
      setEmail("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg || "Could not subscribe. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-amber-500/10 mt-32"
    >
      {/* Newsletter */}
      <div className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-amber-400 font-bold mb-4">
              Stay in the world
            </p>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05]">
              Get first access to <br />
              <span className="text-amber-400">immersive drops</span>,
              experiences <br className="hidden sm:block" />
              and LEVEL UP intakes.
            </h3>
          </div>
          <form
            onSubmit={submit}
            data-testid="footer-newsletter-form"
            className="flex flex-col gap-3 w-full"
          >
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-zinc-950 border border-white/10 px-5 py-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-button"
                disabled={loading}
                className="bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-bold uppercase tracking-[0.25em] text-xs px-7 py-4 inline-flex items-center justify-center gap-2 transition-colors"
              >
                {loading ? "Joining…" : "Join the mailing list"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Or
              </span>
              <span className="h-px flex-1 bg-white/5" />
              <StayConnected variant="footer" pageSource="footer" testid="footer-stay" />
            </div>
          </form>
        </div>
      </div>

      {/* Footer grid */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <img
              src={TOK_LOGO}
              alt="Temple of Kulture"
              className="h-12 w-12 rounded-full object-cover ring-1 ring-amber-400/40"
            />
            <span className="font-display font-black tracking-[0.2em] text-sm uppercase">
              Temple of <span className="text-amber-400">Kulture</span>
            </span>
          </div>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            An immersive cultural ecosystem blending XR, projection, gaming
            culture, education, nightlife and community — rooted in global
            majority stories.
          </p>
          <div className="flex items-center flex-wrap gap-3 mt-6">
            {SOCIAL_LINKS.map(({ key, label, href, Icon }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-social-${key}`}
                aria-label={`Follow Temple of Kulture on ${label}`}
                className="inline-flex h-10 w-10 items-center justify-center border border-white/10 hover:border-amber-400 hover:text-amber-400 transition-colors"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
          <p className="mt-5 text-[11px] uppercase tracking-[0.3em] text-zinc-500">
            Short-form drops on{" "}
            <a
              href="https://www.tiktok.com/@templeofkulture"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-tiktok-callout"
              className="text-amber-400 hover:text-amber-300"
            >
              TikTok
            </a>{" "}
            — LEVEL UP, projection BTS, Ubuntu vibes.
          </p>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold mb-5">
            Explore
          </p>
          <ul className="space-y-3">
            {NAV.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="text-sm text-zinc-300 hover:text-amber-400 transition-colors"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold mb-5">
            More
          </p>
          <ul className="space-y-3">
            {NAV_MORE.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="text-sm text-zinc-300 hover:text-amber-400 transition-colors"
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/ubuntu-retreat"
                data-testid="footer-ubuntu-link"
                className="text-sm text-zinc-300 hover:text-amber-400 transition-colors"
              >
                Ubuntu Retreat
              </Link>
            </li>
            <li>
              <Link
                to="/home-education"
                data-testid="footer-home-education-link"
                className="text-sm text-zinc-300 hover:text-amber-400 transition-colors"
              >
                Home Education
              </Link>
            </li>
            <li>
              <Link
                to="/after-school-clubs"
                data-testid="footer-after-school-clubs-link"
                className="text-sm text-zinc-300 hover:text-amber-400 transition-colors"
              >
                After School Clubs
              </Link>
            </li>
            <li>
              <Link
                to="/interactive-playground"
                data-testid="footer-interactive-playground-link"
                className="text-sm text-zinc-300 hover:text-amber-400 transition-colors"
              >
                Interactive Playground
              </Link>
            </li>
            <li>
              <Link
                to="/kulture-lab"
                data-testid="footer-kulture-lab-link"
                className="text-sm text-zinc-300 hover:text-amber-400 transition-colors"
              >
                Kulture Lab
              </Link>
            </li>
            <li>
              <Link
                to="/join"
                data-testid="footer-join-link"
                className="text-sm text-amber-400 hover:text-amber-300"
              >
                Join the Temple →
              </Link>
            </li>
            <li>
              <Link
                to="/corporate"
                className="text-sm text-amber-400 hover:text-amber-300"
              >
                Corporate Enquiries →
              </Link>
            </li>
            <li>
              <Link
                to="/policies"
                data-testid="footer-policies-link"
                className="text-sm text-zinc-300 hover:text-amber-400 transition-colors inline-flex items-center gap-2"
              >
                <Shield className="h-3 w-3" />
                Policies & Safeguarding
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Discreet funder / supporter strip — real ecosystem credibility */}
      <div className="border-t border-amber-400/10 bg-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8 grid lg:grid-cols-[auto,1fr] gap-x-10 gap-y-4 items-center">
          <p
            data-testid="footer-supporters-label"
            className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold"
          >
            Funded · supported · in partnership with
          </p>
          <div
            data-testid="footer-supporters"
            className="flex flex-wrap gap-x-7 gap-y-2 items-baseline"
          >
            {getTrustBandPartners().map(
              (p) => (
                <span
                  key={p.id}
                  data-testid={`footer-supporter-${p.id}`}
                  className="font-display font-bold uppercase tracking-tight text-zinc-300 text-sm sm:text-base whitespace-nowrap"
                >
                  {p.name}
                </span>
              ),
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between text-xs text-zinc-500">
          <div className="space-y-3">
            <p>© {new Date().getFullYear()} Temple of Kulture. All rights reserved.</p>
            <PhilosophyQuote
              variant="whisper"
              lineKey="without-stories"
              testId="footer-philosophy"
              className="block"
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.25em]">
            <Link
              to="/policies/safeguarding"
              className="hover:text-amber-400 transition-colors"
              data-testid="footer-link-safeguarding"
            >
              Safeguarding
            </Link>
            <Link
              to="/policies/gdpr-privacy"
              className="hover:text-amber-400 transition-colors"
              data-testid="footer-link-gdpr"
            >
              GDPR
            </Link>
            <Link
              to="/policies/accessibility"
              className="hover:text-amber-400 transition-colors"
              data-testid="footer-link-accessibility"
            >
              Accessibility
            </Link>
            <Link
              to="/policies/immersive-safety"
              className="hover:text-amber-400 transition-colors"
              data-testid="footer-link-immersive-safety"
            >
              Immersive Safety
            </Link>
            <span>Built for the diaspora future.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
